/**
 * product-form.js — Product page logic for the Hydrodiff Shopify theme.
 *
 * Handles:
 *   - Product image gallery (thumbnails, slider, fullscreen)
 *   - Variant option selection
 *   - AJAX add-to-cart via the global CartAPI
 *   - "Buy now" direct checkout
 *   - Read more / less for description
 *   - Delivery date estimation (today + 14 business days)
 *
 * Dependencies: global.js (CartAPI, PubSub)
 */

document.addEventListener('DOMContentLoaded', () => {
  /* --------------------------------------------------
     1. Parse the product JSON embedded in the page
     -------------------------------------------------- */

  const jsonScript = document.querySelector('script[data-product-json]');
  if (!jsonScript) return;

  let product;
  try {
    product = JSON.parse(jsonScript.textContent);
  } catch (err) {
    console.error('[product-form] Could not parse product JSON:', err);
    return;
  }

  /* Ensure variant prices are integers (centimes). Shopify JSON may serialize as "279989.00" strings. */
  product.variants.forEach(function(v) {
    v.price = parseInt(v.price, 10) || 0;
    v.compare_at_price = parseInt(v.compare_at_price, 10) || 0;
  });

  /* --------------------------------------------------
     2. DOM references
     -------------------------------------------------- */

  const optionSelects = document.querySelectorAll('[data-option-select]');
  const variantIdInput = document.querySelector('[data-variant-id]');
  const priceDisplay = document.querySelector('[data-product-price]');
  const comparePriceDisplay = document.querySelector('[data-compare-price]');
  const subtotalPrice = document.querySelector('[data-subtotal-price]');
  const subtotalCompare = document.querySelector('[data-subtotal-compare]');
  const addToCartBtn = document.querySelector('[data-add-to-cart]');
  const addToCartText = addToCartBtn
    ? addToCartBtn.querySelector('[data-add-to-cart-text]') || addToCartBtn
    : null;
  const buyNowBtn = document.querySelector('[data-buy-now]');
  const buyNowText = buyNowBtn
    ? buyNowBtn.querySelector('[data-buy-now-text]') || buyNowBtn
    : null;
  const productForm = document.querySelector('[data-product-form]');

  /* --------------------------------------------------
     3. Variant matching
     -------------------------------------------------- */

  function getSelectedOptions() {
    const selected = [];
    optionSelects.forEach((el) => {
      const index = parseInt(el.getAttribute('data-option-index'), 10);
      if (el.tagName === 'SELECT') {
        selected[index] = el.value;
      } else {
        const checked = el.querySelector('input[type="radio"]:checked');
        if (checked) selected[index] = checked.value;
      }
    });
    return selected;
  }

  function findVariant(options) {
    return product.variants.find((variant) =>
      options.every((value, index) => variant.options[index] === value)
    );
  }

  /* --------------------------------------------------
     4. UI update for variant changes
     (formatMoney is now global — defined in global.js)
     -------------------------------------------------- */

  /* Track current variant for subtotal calculations */
  let currentVariant = product.variants.find(function(v) {
    return variantIdInput && v.id === parseInt(variantIdInput.value, 10);
  }) || product.variants[0];

  /* Accessory items */
  var accessoryItems = document.querySelectorAll('[data-accessory-item]');
  var accessoryTotalCount = accessoryItems.length;

  function updateSubtotal() {
    if (!currentVariant) return;

    var qtyEl = document.querySelector('[data-quantity-input]');
    var qty = Math.max(1, parseInt(qtyEl && qtyEl.value, 10) || 1);
    var mainPrice = currentVariant.price * qty;
    var mainCompare = Math.max(currentVariant.compare_at_price || 0, currentVariant.price) * qty;

    var accPrice = 0;
    var accCompare = 0;
    document.querySelectorAll('[data-accessory-item].is-selected').forEach(function(item) {
      var p = parseInt(item.dataset.accessoryPrice, 10) || 0;
      var cp = parseInt(item.dataset.accessoryComparePrice, 10) || 0;
      accPrice += p;
      accCompare += cp > 0 ? cp : p;
    });

    var totalPrice = mainPrice + accPrice;
    var totalCompare = mainCompare + accCompare;

    if (subtotalPrice) {
      subtotalPrice.setAttribute('data-price-cents', totalPrice);
      subtotalPrice.textContent = formatMoney(getDisplayPrice(totalPrice)) + ' ' + getTaxLabel();
    }

    if (subtotalCompare) {
      if (totalCompare > totalPrice) {
        subtotalCompare.setAttribute('data-price-cents', totalCompare);
        subtotalCompare.innerHTML = '<s>' + formatMoney(getDisplayPrice(totalCompare)) + ' ' + getTaxLabel() + '</s>';
        subtotalCompare.style.display = '';
      } else {
        subtotalCompare.style.display = 'none';
      }
    }
  }

  function updateCartButtonText() {
    if (!addToCartText || !addToCartBtn || addToCartBtn.disabled) return;

    var checkedCount = document.querySelectorAll('[data-accessory-item].is-selected').length;

    var text;
    if (checkedCount === 0) {
      text = 'Ajouter le produit au panier';
    } else if (checkedCount > 0 && checkedCount < accessoryTotalCount) {
      text = 'Ajouter les produits au panier';
    } else {
      text = 'Ajouter le pack complet au panier';
    }
    addToCartText.textContent = text;
  }

  function updateUI(variant) {
    currentVariant = variant;

    if (variantIdInput) {
      variantIdInput.value = variant ? variant.id : '';
    }

    if (priceDisplay && variant) {
      priceDisplay.setAttribute('data-price-cents', variant.price);
      priceDisplay.textContent = formatMoney(getDisplayPrice(variant.price)) + ' ' + getTaxLabel();
    }

    if (comparePriceDisplay) {
      if (variant && variant.compare_at_price && variant.compare_at_price > variant.price) {
        comparePriceDisplay.setAttribute('data-price-cents', variant.compare_at_price);
        comparePriceDisplay.innerHTML = '<s>' + formatMoney(getDisplayPrice(variant.compare_at_price)) + ' ' + getTaxLabel() + '</s>';
        comparePriceDisplay.style.display = '';
      } else {
        comparePriceDisplay.style.display = 'none';
      }
    }

    if (addToCartBtn) {
      if (!variant || !variant.available) {
        addToCartBtn.disabled = true;
        if (addToCartText) addToCartText.textContent = !variant ? 'Non disponible' : 'Épuisé';
      } else {
        addToCartBtn.disabled = false;
        updateCartButtonText();
      }
    }

    if (buyNowBtn) {
      buyNowBtn.disabled = !variant || !variant.available;
    }

    if (variant) {
      const url = new URL(window.location);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState({}, '', url.toString());
    }

    updateSubtotal();
  }

  /* --------------------------------------------------
     5b. Accessory toggle
     -------------------------------------------------- */

  accessoryItems.forEach(function(item) {
    function toggleAccessory() {
      item.classList.toggle('is-selected');
      item.setAttribute('aria-checked', String(item.classList.contains('is-selected')));
      updateSubtotal();
      updateCartButtonText();
    }
    item.addEventListener('click', toggleAccessory);
    item.addEventListener('keydown', function(e) {
      if (e.target !== item) return;
      if (e.key === ' ' || e.key === 'Enter') {
        e.preventDefault();
        toggleAccessory();
      }
    });
  });

  /* --------------------------------------------------
     6. Option change listeners
     -------------------------------------------------- */

  optionSelects.forEach((el) => {
    el.addEventListener('change', () => {
      const options = getSelectedOptions();
      const variant = findVariant(options);
      updateUI(variant);
    });
  });

  /* --------------------------------------------------
     7. AJAX add-to-cart
     -------------------------------------------------- */

  /** Build cart items array: main product + checked accessories */
  function buildCartItems() {
    var variantId = variantIdInput ? variantIdInput.value : null;
    if (!variantId) return [];

    var quantityInput = document.querySelector('[data-quantity-input]');
    var quantity = quantityInput ? parseInt(quantityInput.value, 10) || 1 : 1;

    var items = [{ id: parseInt(variantId, 10), quantity: quantity }];

    document.querySelectorAll('[data-accessory-item].is-selected').forEach(function(item) {
      items.push({ id: parseInt(item.dataset.accessoryVariantId, 10), quantity: 1 });
    });

    return items;
  }

  if (productForm) {
    productForm.addEventListener('submit', async (e) => {
      e.preventDefault();

      var items = buildCartItems();
      if (items.length === 0) return;

      if (addToCartBtn) {
        addToCartBtn.disabled = true;
        if (addToCartText) addToCartText.textContent = 'Ajout en cours…';
      }

      try {
        await CartAPI.add(items);
        const cart = await CartAPI.get();
        const cartCount = document.querySelector('[data-cart-count]');
        if (cartCount) cartCount.textContent = cart.item_count;

        /* Open cart drawer if available */
        PubSub.publish('cart-drawer:open', {});

        if (addToCartText) addToCartText.textContent = 'Ajouté !';
        setTimeout(() => {
          if (addToCartBtn && addToCartText) {
            addToCartBtn.disabled = false;
            updateCartButtonText();
          }
        }, 1500);
      } catch (err) {
        console.error('[product-form] Add to cart failed:', err);
        if (addToCartText) addToCartText.textContent = 'Erreur — réessayer';
        setTimeout(() => {
          if (addToCartBtn && addToCartText) {
            addToCartBtn.disabled = false;
            updateCartButtonText();
          }
        }, 2000);
      }
    });
  }

  /* --------------------------------------------------
     8. Buy Now — direct checkout
     -------------------------------------------------- */

  if (buyNowBtn) {
    buyNowBtn.addEventListener('click', async () => {
      var items = buildCartItems();
      if (items.length === 0) return;

      buyNowBtn.disabled = true;
      if (buyNowText) buyNowText.textContent = 'Redirection…';

      try {
        await CartAPI.add(items);
        window.location.href = '/checkout';
      } catch (err) {
        console.error('[product-form] Buy now failed:', err);
        buyNowBtn.disabled = false;
        if (buyNowText) buyNowText.textContent = 'Acheter maintenant';
      }
    });
  }

  /* --------------------------------------------------
     9. Image Gallery
     -------------------------------------------------- */

  const gallery = document.querySelector('[data-product-gallery]');
  if (gallery) {
    const thumbs = gallery.querySelectorAll('[data-thumb-index]');
    const slides = gallery.querySelectorAll('[data-slide-index]');
    const prevBtn = gallery.querySelector('[data-gallery-prev]');
    const nextBtn = gallery.querySelector('[data-gallery-next]');
    let currentSlide = 0;
    const totalSlides = slides.length || 1;

    function goToSlide(index) {
      if (index < 0) index = totalSlides - 1;
      if (index >= totalSlides) index = 0;
      currentSlide = index;

      slides.forEach((slide, i) => {
        slide.classList.toggle('product-gallery__slide--active', i === index);
      });
      thumbs.forEach((thumb, i) => {
        thumb.classList.toggle('product-gallery__thumb--active', i === index);
      });
    }

    thumbs.forEach((thumb) => {
      thumb.addEventListener('click', () => {
        goToSlide(parseInt(thumb.getAttribute('data-thumb-index'), 10));
      });
    });

    if (prevBtn) prevBtn.addEventListener('click', () => goToSlide(currentSlide - 1));
    if (nextBtn) nextBtn.addEventListener('click', () => goToSlide(currentSlide + 1));

    /* -- Fullscreen -- */
    const fsOverlay = document.querySelector('[data-fullscreen-overlay]');
    const fsImage = document.querySelector('[data-fullscreen-image]');
    const fsClose = document.querySelector('[data-fullscreen-close]');
    const fsPrev = document.querySelector('[data-fullscreen-prev]');
    const fsNext = document.querySelector('[data-fullscreen-next]');
    const fsBtn = gallery.querySelector('[data-gallery-fullscreen]');

    function openFullscreen() {
      if (!fsOverlay || !fsImage) return;
      const activeSlide = gallery.querySelector('.product-gallery__slide--active img');
      if (!activeSlide) return;

      const img = document.createElement('img');
      img.src = activeSlide.src;
      img.srcset = activeSlide.srcset || '';
      img.alt = activeSlide.alt || '';
      fsImage.innerHTML = '';
      fsImage.appendChild(img);

      fsOverlay.classList.add('product-fullscreen--active');
      fsOverlay.setAttribute('aria-hidden', 'false');
      document.body.style.overflow = 'hidden';
    }

    function closeFullscreen() {
      if (!fsOverlay) return;
      fsOverlay.classList.remove('product-fullscreen--active');
      fsOverlay.setAttribute('aria-hidden', 'true');
      document.body.style.overflow = '';
    }

    function fsNavigate(dir) {
      goToSlide(currentSlide + dir);
      openFullscreen();
    }

    if (fsBtn) fsBtn.addEventListener('click', openFullscreen);
    if (fsClose) fsClose.addEventListener('click', closeFullscreen);
    if (fsPrev) fsPrev.addEventListener('click', () => fsNavigate(-1));
    if (fsNext) fsNext.addEventListener('click', () => fsNavigate(1));

    if (fsOverlay) {
      fsOverlay.addEventListener('click', (e) => {
        if (e.target === fsOverlay) closeFullscreen();
      });
      document.addEventListener('keydown', (e) => {
        if (!fsOverlay.classList.contains('product-fullscreen--active')) return;
        if (e.key === 'Escape') closeFullscreen();
        if (e.key === 'ArrowLeft') fsNavigate(-1);
        if (e.key === 'ArrowRight') fsNavigate(1);
      });
    }

    // Gallery also navigable in inline view with keyboard
    document.addEventListener('keydown', (e) => {
      if (fsOverlay && fsOverlay.classList.contains('product-fullscreen--active')) return;
      if (e.key === 'ArrowLeft' && prevBtn) goToSlide(currentSlide - 1);
      if (e.key === 'ArrowRight' && nextBtn) goToSlide(currentSlide + 1);
    });
  }

  /* --------------------------------------------------
     10. Read more / less for description
     -------------------------------------------------- */

  const descText = document.querySelector('[data-description-text]');
  const readMoreBtn = document.querySelector('[data-read-more]');

  if (descText && readMoreBtn) {
    let expanded = false;
    readMoreBtn.addEventListener('click', () => {
      expanded = !expanded;
      descText.classList.toggle('product-info__description-text--expanded', expanded);
      readMoreBtn.textContent = expanded ? 'Lire moins' : 'Lire plus…';
    });
  }

  /* --------------------------------------------------
     10b. Wrap heading + bullet-list pairs in outline cards
     -------------------------------------------------- */

  if (descText) {
    var headings = descText.querySelectorAll('h2, h3, h4');
    headings.forEach(function(heading) {
      var next = heading.nextElementSibling;
      if (!next || next.tagName !== 'UL') return;

      /* Collect all consecutive UL elements (may be split by whitespace nodes or <br>) */
      var wrapper = document.createElement('div');
      wrapper.className = 'product-info__desc-card';
      heading.parentNode.insertBefore(wrapper, heading);
      wrapper.appendChild(heading);

      var sibling = wrapper.nextSibling;
      while (sibling) {
        var nextSibling = sibling.nextSibling;
        /* Keep grabbing UL elements and whitespace/br between them */
        if (sibling.nodeType === 3 && sibling.textContent.trim() === '') {
          /* Empty text node — grab it and continue */
          wrapper.appendChild(sibling);
        } else if (sibling.nodeType === 1 && sibling.tagName === 'UL') {
          wrapper.appendChild(sibling);
        } else if (sibling.nodeType === 1 && sibling.tagName === 'BR') {
          wrapper.appendChild(sibling);
        } else {
          /* Hit a paragraph, heading, or other element — stop */
          break;
        }
        sibling = nextSibling;
      }
    });
  }

  /* --------------------------------------------------
     11. Quantity selector wiring (local, for product page)
     -------------------------------------------------- */

  const qtyInput = document.querySelector('[data-quantity-input]');

  if (qtyInput) {
    qtyInput.addEventListener('input', updateSubtotal);
    qtyInput.addEventListener('change', () => {
      const n = Math.floor(Number(qtyInput.value));
      qtyInput.value = (isNaN(n) || n < 1) ? 1 : n;
      updateSubtotal();
    });
  }

  /* --------------------------------------------------
     13. Initial state
     -------------------------------------------------- */

  /* --------------------------------------------------
     13b. Listen for HT/TTC toggle changes
     -------------------------------------------------- */

  PubSub.subscribe('tax-mode-change', function () {
    if (currentVariant) updateUI(currentVariant);
  });

  /* --------------------------------------------------
     14. Initial state
     -------------------------------------------------- */

  (function setInitialVariant() {
    const params = new URLSearchParams(window.location.search);
    const variantParam = params.get('variant');
    let variant;

    if (variantParam) {
      variant = product.variants.find((v) => v.id.toString() === variantParam);
    }
    if (!variant) {
      const options = getSelectedOptions();
      variant = findVariant(options);
    }
    if (!variant) {
      variant = product.variants.find((v) => v.available) || product.variants[0];
    }
    if (variant) {
      updateUI(variant);
    }
  })();
});
